import React, { useState, useEffect } from 'react';
import { Document as PdfDocument, Page, pdfjs } from 'react-pdf';
import { Document } from '../types'; // Import from types.ts

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const EventManager = () => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [r2Objects, setR2Objects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
    const [isStaging, setIsStaging] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const API_BASE_URL = ''; // Use relative path for API calls

    useEffect(() => {
        loadDocuments();
        loadR2Objects();
    }, []);

    const loadDocuments = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/document-staging`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.json() as Document[];
            setDocuments(data);
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('An unknown error occurred');
            }
        } finally {
            setLoading(false);
        }
    };

    const loadR2Objects = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/r2-objects?recursive=true`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            setR2Objects(data.objects || []);
        } catch (err) {
            console.error('Error loading R2 objects:', err);
        }
    };

    const handleEdit = (doc: Document) => {
        setSelectedDocument(doc);
        setIsModalOpen(true);
    };

    const handleSave = async (updatedDoc: Document) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/documents/${updatedDoc.id}/update`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedDoc),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error((data as any).message || `HTTP ${response.status}`);
            }
            setIsModalOpen(false);
            loadDocuments(); // Refresh documents list
        } catch (err) {
            if (err instanceof Error) {
                alert('Error saving document: ' + err.message);
            } else {
                alert('An unknown error occurred');
            }
        }
    };

    const handleCreateEvent = async (eventData: EventFormData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/events`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(eventData),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error((data as any).message || `HTTP ${response.status}`);
            }
            setIsCreateModalOpen(false);
            loadDocuments(); // Refresh the list
        } catch (err) {
            if (err instanceof Error) {
                alert('Error creating event: ' + err.message);
            } else {
                alert('An unknown error occurred');
            }
        }
    };


    const handleStageFromR2 = async () => {
        setIsStaging(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/stage-documents-from-r2`, {
                method: 'POST',
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error((data as any).message || `HTTP ${response.status}`);
            }
            loadDocuments(); // Refresh documents list after staging
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('An unknown error occurred during staging');
            }
        } finally {
            setIsStaging(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Event Manager</h1>
                        <p className="text-gray-600 mt-1">Link documents to R2 PDFs and manage event metadata</p>
                    </div>
                    <div className="flex space-x-3">
                        <button onClick={() => setIsCreateModalOpen(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                            Create New Event
                        </button>
                        <button onClick={handleStageFromR2} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2" disabled={isStaging}>
                            {isStaging ? (
                                <span className="animate-spin">⟳</span>
                            ) : (
                                <span>📤</span>
                            )}
                            <span>{isStaging ? 'Staging...' : 'Stage from R2'}</span>
                        </button>
                        <button onClick={loadDocuments} className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
                            Refresh Documents
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading && <p>Loading documents...</p>}

            {/* Error State */}
            {error && <p className="text-red-500">{error}</p>}

            {/* Documents List */}
            {!loading && !error && (
                <div id="documentsList" className="space-y-4">
                    {documents.map(doc => (
                        <DocumentCard key={doc.id} doc={doc} onEdit={() => handleEdit(doc)} />
                    ))}
                </div>
            )}

            {/* Edit Modal */}
            {isModalOpen && selectedDocument && (
                <EditModal
                    doc={selectedDocument}
                    r2Objects={r2Objects}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSave}
                />
            )}

            {isCreateModalOpen && (
                <CreateEventModal
                    r2Objects={r2Objects}
                    onClose={() => setIsCreateModalOpen(false)}
                    onSave={handleCreateEvent}
                />
            )}
        </div>
    );
};

const DocumentCard = ({ doc, onEdit }: { doc: Document, onEdit: () => void }) => {
    const getTypeColor = (type: string) => {
        // ... (same as in the original HTML)
        return 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-medium text-gray-900 truncate">{doc.title || doc.file_name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{doc.file_name}</p>
                    <div className="flex items-center space-x-4 mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(doc.document_type || 'Unknown')}`}>
                            {doc.document_type || 'Unknown'}
                        </span>
                        <span className="text-xs text-gray-500">
                            {doc.document_date ? new Date(doc.document_date).toLocaleDateString() : 'No date'}
                        </span>
                        {doc.r2_key ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✓ Linked to R2
                            </span>
                        ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                ⚠ Not linked
                            </span>
                        )}
                    </div>
                    {doc.r2_key && (
                        <div className="mt-2 text-xs text-gray-600">
                            <strong>R2 Key:</strong> {doc.r2_key}
                        </div>
                    )}
                </div>
                <div className="ml-4 flex-shrink-0">
                    <button onClick={onEdit} className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors">
                        {doc.r2_key ? 'Edit Link' : 'Link to R2'}
                    </button>
                </div>
            </div>
        </div>
    );
};

interface EventFormData {
    event_date: string;
    event_type: string;
    title: string;
    details: string;
    r2_keys: string[];
}

const CreateEventModal = ({ r2Objects, onClose, onSave }: { r2Objects: any[], onClose: () => void, onSave: (event: EventFormData) => void }) => {
    const [formData, setFormData] = useState<EventFormData>({
        event_date: '',
        event_type: '',
        title: '',
        details: '',
        r2_keys: [],
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleMultiSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
        setFormData(prev => ({ ...prev, r2_keys: selectedOptions }));
    };

    const handleSubmit = () => {
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg font-medium text-gray-900">Create New Event</h3>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Event Date</label>
                                <input type="date" name="event_date" value={formData.event_date} onChange={handleChange} className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Event Type</label>
                                <input type="text" name="event_type" value={formData.event_type} onChange={handleChange} className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Title</label>
                                <input type="text" name="title" value={formData.title} onChange={handleChange} className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Details</label>
                                <textarea name="details" value={formData.details} onChange={handleChange} rows={3} className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2"></textarea>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Associated Documents (select multiple)</label>
                                <select multiple name="r2_keys" value={formData.r2_keys} onChange={handleMultiSelectChange} className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 h-32">
                                    {r2Objects.map((obj: { key: string }) => (
                                        <option key={obj.key} value={obj.key}>{obj.key}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button onClick={handleSubmit} className="bg-blue-600 text-white px-4 py-2 rounded-lg">Save</button>
                        <button onClick={onClose} className="ml-3 bg-white px-4 py-2 rounded-lg border">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const EditModal = ({ doc, r2Objects, onClose, onSave }: { doc: Document, r2Objects: any[], onClose: () => void, onSave: (doc: Document) => void }) => {
    const [formData, setFormData] = useState({
        r2_key: doc.r2_key || '',
        r2_bucket: doc.r2_bucket || 'termination-app-documents',
        file_size: doc.file_size || '',
        document_type: doc.document_type || '',
    });
    const [analysis, setAnalysis] = useState('');
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [text, setText] = useState(''); // To store extracted text from PDF

    const onDocumentLoadSuccess = async ({ numPages }: { numPages: number }) => {
        if (!doc.r2_key) return; // Add guard clause here

        let fullText = '';
        const pdf = await pdfjs.getDocument(doc.r2_key).promise;
        for (let i = 1; i <= numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            fullText += textContent.items.map((item: any) => item.str ?? '').join(' ');
        }
        setText(fullText);
    };

    const handleAnalyze = async () => {
        const response = await fetch('/api/ai/analyze-document', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text }),
        });
        const data = await response.json();
        setAnalysis(data.response);
    };

    const handleAskQuestion = async () => {
        const response = await fetch('/api/ai/q-and-a', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text, question }),
        });
        const data = await response.json();
        setAnswer(data.response);
    };

    const handleR2Select = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedKey = e.target.value;
        const selectedObj = r2Objects.find((obj: any) => obj.key === selectedKey);
        if (selectedObj) {
            setFormData(prev => ({
                ...prev,
                r2_key: selectedKey,
                file_size: selectedObj.size,
                document_type: selectedObj.httpMetadata?.contentType || 'application/pdf',
            }));
        }
    };

    const handleSubmit = () => {
        onSave({ ...doc, ...formData, file_size: parseInt(String(formData.file_size), 10) || 0 });
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    {/* Modal content */}
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg font-medium text-gray-900">Edit Document</h3>
                        {/* Form fields */}
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">R2 Object</label>
                                <select name="r2_key" value={formData.r2_key} onChange={handleR2Select} className="w-full mt-1">
                                    <option value="">Select an R2 object...</option>
                                    {r2Objects.map((obj: { key: string }) => (
                                        <option key={obj.key} value={obj.key}>{obj.key}</option>
                                    ))}
                                </select>
                            </div>
                            {/* PDF Preview */}
                            {formData.r2_key && (
                                <div>
                                    <h4 className="text-md font-medium text-gray-900">Document Preview</h4>
                                    <div className="mt-2 border p-2 h-96 overflow-auto">
                                        <PdfDocument file={`/api/r2-proxy/${formData.r2_key}`} onLoadSuccess={onDocumentLoadSuccess}>
                                            <Page pageNumber={1} />
                                        </PdfDocument>
                                    </div>
                                </div>
                            )}
                            {/* AI Analysis */}
                            <div>
                                <button onClick={handleAnalyze} className="bg-purple-600 text-white px-4 py-2 rounded-lg">Analyze Document</button>
                                {analysis && <div className="mt-2 border p-2 bg-gray-100">{analysis}</div>}
                            </div>
                            {/* Q&A */}
                            <div>
                                <input
                                    type="text"
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                                    placeholder="Ask a question about the document..."
                                />
                                <button onClick={handleAskQuestion} className="mt-2 bg-purple-600 text-white px-4 py-2 rounded-lg">Ask</button>
                                {answer && <div className="mt-2 border p-2 bg-gray-100">{answer}</div>}
                            </div>
                            {/* Other form fields... */}
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button onClick={handleSubmit} className="bg-blue-600 text-white px-4 py-2 rounded-lg">Save Changes</button>
                        <button onClick={onClose} className="ml-3 bg-white px-4 py-2 rounded-lg border">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventManager;
